import styled, { css } from 'styled-components';
import { IconContainer } from '../../icon';
import { pxToRem } from '../../../design-tokens/style-utilities';
const HORIZONTAL_SCROLL_OVERFLOW_WIDTH = 150;
const HORIZONTAL_SCROLL_HITBOX_OFFSET = 15;
const HORIZONTAL_SCROLL_BUTTON_SIZE = 40;
const NAVIGATION_BUTTON_OFFSET = 2;
// Buttons should not be affected by the fades
const Z_INDEX_FADES = 2;
const Z_INDEX_BUTTONS = 3;
// Size is the width of the viewport minus the width of the scrollbar
const TOTAL_AVAILABLE_WIDTH = 'calc(100vw - var(--scrollbar-width))';
// Space is the amount of space that is available on the left and right side of the container
const TOTAL_AVAILABLE_SPACE = `calc((${TOTAL_AVAILABLE_WIDTH} - var(--parent-width)) / 2)`;
// Overflow width is ideally the value of HORIZONTAL_SCROLL_OVERFLOW_WIDTH but is calculated with the available space in mind
const OVERFLOW_WIDTH = `min(${HORIZONTAL_SCROLL_OVERFLOW_WIDTH}px, ${TOTAL_AVAILABLE_SPACE})`;
// The width of the scroll wrapper
const WRAPPER_WIDTH = `calc(var(--parent-width) + (2 * ${OVERFLOW_WIDTH}))`;
/**
 * HorizontalScrollWrapper
 */
export const horizontalScrollWrapperStyles = ({ theme }) => css `
	// This value will be overwritten by JavaScript
	--scrollbar-width: 0px;
	// This value will be overwritten by JavaScript
	--parent-width: 100%;
	position: relative;
	z-index: 0;
	margin-left: calc(${OVERFLOW_WIDTH} * -1);
	overflow: hidden;
	width: ${WRAPPER_WIDTH};
	/*
	This padding is added and then removed via a negative margin to counter the overflow hidden.
	The increased hitbox for the scrollbar needs some room at the bottom, but we don't want to
	have extra spacing underneath the entire component
	*/
	padding-bottom: ${pxToRem(HORIZONTAL_SCROLL_HITBOX_OFFSET)};
	margin-bottom: -${pxToRem(HORIZONTAL_SCROLL_HITBOX_OFFSET)};

	&:hover ${HorizontalScrollButton} {
		opacity: 1;
	}

	// We need to know for sure that the button can be displayed with a negative transform
	@media (min-width: ${pxToRem(Number(theme.containerWidth.containerWidthMax) + 2 * HORIZONTAL_SCROLL_BUTTON_SIZE)}) {
		//
	}
`;
export const HorizontalScrollWrapper = styled.div `
	${horizontalScrollWrapperStyles}
`;
/**
 * HorizontalScrollContainer
 */
export const horizontalScrollContainerStyles = ({ theme }) => css `
	margin: 0 auto;
	position: relative;
	width: calc(100% - (2 * ${OVERFLOW_WIDTH}));

	.swiper-scrollbar {
		position: relative;

		&::after {
			content: '';
			position: absolute;
			top: -${pxToRem(HORIZONTAL_SCROLL_HITBOX_OFFSET)};
			right: -${pxToRem(HORIZONTAL_SCROLL_HITBOX_OFFSET)};
			bottom: -${pxToRem(HORIZONTAL_SCROLL_HITBOX_OFFSET)};
			left: -${pxToRem(HORIZONTAL_SCROLL_HITBOX_OFFSET)};
		}
	}

	// Add fades left and right of main scrollbar
	@media (min-width: ${pxToRem(theme.viewportBreakpoint.md)}) {
		&::before,
		&::after {
			z-index: ${Z_INDEX_FADES};
			content: '';
			position: absolute;
			top: 0;
			bottom: 0;
			width: ${OVERFLOW_WIDTH};
			background: ${theme.colors.gradients.overlayHorizontalScroll};
			pointer-events: none;
		}

		&::before {
			left: 0;
			transform: translateX(-100%) rotate(180deg);
		}

		&::after {
			right: 0;
			transform: translateX(100%);
		}
	}
`;
export const HorizontalScrollContainer = styled.div `
	${horizontalScrollContainerStyles}
`;
export const horizontalScrollButtonStyles = ({ theme, $position, }) => css `
	display: none;
	align-items: center;
	justify-content: center;
	background: ${theme.colors.base.bgButton};
	border: none;
	border-radius: ${theme.borderRadius.rounded}%;
	color: ${theme.colors.blanc.iconUi};
	height: ${pxToRem(HORIZONTAL_SCROLL_BUTTON_SIZE)};
	opacity: 0;
	transition: opacity 150ms ease-in-out;
	width: ${pxToRem(HORIZONTAL_SCROLL_BUTTON_SIZE)};

	${IconContainer} {
		flex: none;
	}

	&:hover,
	&:focus {
		background: ${theme.colors.base.bgButtonHover};
		color: ${theme.colors.blanc.iconUi};
	}

	${$position &&
    css `
		position: absolute;
		top: calc(50% - ${pxToRem(HORIZONTAL_SCROLL_BUTTON_SIZE / 2)});
		z-index: ${Z_INDEX_BUTTONS};
	`}

	${$position === 'left' &&
    css `
		left: 0;
		transform: translate(-50%, -50%);

		${IconContainer} {
			// We need to offset the icon because a centered chevron has an optical illusion that makes it look off-centre
			margin-left: -${pxToRem(NAVIGATION_BUTTON_OFFSET)};
		}
	`}

	${$position === 'right' &&
    css `
		right: 0;
		transform: translate(50%, -50%);

		${IconContainer} {
			// We need to offset the icon because a centered chevron has an optical illusion that makes it look off-centre
			margin-left: ${pxToRem(NAVIGATION_BUTTON_OFFSET)};
		}
	`}

	@media (min-width: ${pxToRem(theme.viewportBreakpoint.md)}) {
		display: flex;
	}
`;
export const HorizontalScrollButton = styled.button `
	${horizontalScrollButtonStyles}
`;
